import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  api,
  type PhoneCall,
  type PhoneCallDirection,
  type PhoneCallListParams,
  type PhoneCallsStatus,
} from "../lib/api";
import { useDebounced } from "../lib/media";
import { queryKeys } from "../lib/queryKeys";
import { agoPhrase, shortDateTime } from "../lib/time";
import { CallDetail, partyName } from "../components/calls/CallDetail";
import { callDuration, directionLabel } from "../components/calls/call-format";
import { formatPhone } from "../components/crm/PhoneField";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/Button";
import { Select } from "../components/ui/Select";
import { cn } from "../components/ui/cn";
import type { User } from "../types";

/**
 * Every call the phone system handled, searchable, with recordings.
 *
 * The rows are Pulse's copy of Yay's call history, read in by a poll (see
 * `apps/api/src/telephony`), so the list and its search are as fast as any
 * other list here; the recording and the transcript are fetched from Yay only
 * when a call is opened.
 *
 * **The filters live in the URL**, for the reason `ticket-filters.ts` gives:
 * "the missed calls to the support line yesterday" is something somebody
 * pastes to a colleague, and a reload should not lose it. Replaced rather than
 * pushed -- narrowing a list is several goes at a control, and each one should
 * not be a step on the back button.
 *
 * **The open call is in the path**, `/calls/:callId`, with the filters kept
 * behind it, so a link to a call lands on it with the list it was found in.
 * Beside the list on a wide screen and instead of it on a phone, which is the
 * ticket view's arrangement: a pane that narrow beside a list that narrow
 * would leave neither readable.
 */

const PAGE_SIZE = 50;

/** How long after "Sync now" the list is refetched -- a pass is a few calls to Yay. */
const SYNC_SETTLE_MS = 4_000;

export function Calls({ user }: { user: User }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { callId } = useParams();
  const openId = callId ? Number(callId) : null;
  const isAdmin = user.role === "admin";

  const status = useQuery({
    queryKey: queryKeys.phoneCallsStatus,
    queryFn: api.phoneCallsStatus,
    /* A minute, matching the poll: the line under the header says how fresh
       the history is, and it should not itself be the stale thing. */
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const enabled = status.data?.enabled === true;

  /**
   * Apply several filter changes as one navigation -- `CallsList`'s `set`, and
   * for its reason: three single-key setters in one render each start from the
   * same params, and the last one wins.
   */
  const set = (patch: Record<string, string | null>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    /* Any filter change resets paging; page three of a list that now has four
       rows is a blank screen with no explanation. */
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };

  const query = toQuery(params);

  const calls = useQuery({
    queryKey: queryKeys.phoneCalls(query as Record<string, unknown>),
    queryFn: () => api.phoneCalls(query),
    enabled,
    /* Kept while the next page or filter loads, so the list does not blank
       between every keystroke of a search. */
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });

  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  /* The filters travel with the call, so closing it returns to the same list. */
  const open = (id: number): void => {
    void navigate({ pathname: `/calls/${id}`, search: params.toString() });
  };
  const close = (): void => {
    void navigate({ pathname: "/calls", search: params.toString() });
  };

  if (status.isLoading) {
    return <p className="p-8 text-center text-sm text-faint">Loading…</p>;
  }

  if (!enabled) {
    return (
      <EmptyState
        title="The phone system is not connected"
        description="Call history comes from Yay. Until YAY_RESELLER, YAY_USER and YAY_PASSWORD are set on this server there is nothing to show — no calls are being read."
      />
    );
  }

  const page = calls.data;
  const rows = page?.calls ?? [];
  const total = page?.total ?? 0;
  const current = query.page ?? 1;
  const seed = openId === null ? undefined : rows.find((row) => row.id === openId);

  return (
    <div className="flex h-full overflow-hidden">
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col overflow-hidden",
          /* A phone shows the call instead of the list. */
          openId !== null && "hidden lg:flex",
        )}
      >
        <header className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
          <h1 className="text-base font-semibold text-content">Calls</h1>
          <SyncLine status={status.data} isAdmin={isAdmin} />
        </header>

        <Filters params={params} set={set} users={users.data} />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {calls.isLoading ? (
            <p className="p-8 text-center text-sm text-faint">Loading…</p>
          ) : calls.isError ? (
            <p className="p-8 text-center text-sm text-faint">
              The call history could not be read.
            </p>
          ) : rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-faint">
              {hasFilters(params)
                ? "No calls match these filters."
                : "No calls have been read yet. The first sync reaches back ninety days and can take a few minutes."}
            </p>
          ) : (
            <CallTable rows={rows} openId={openId} onOpen={open} />
          )}
        </div>

        <footer className="flex items-center gap-3 border-t border-line px-4 py-2 text-xs text-faint">
          <span>
            {total === 0
              ? "No calls"
              : `${(current - 1) * PAGE_SIZE + 1}–${Math.min(current * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
          </span>
          <span className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={current <= 1}
              onClick={() => set({ page: current - 1 <= 1 ? null : String(current - 1) })}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={current * PAGE_SIZE >= total}
              onClick={() => set({ page: String(current + 1) })}
            >
              Next
            </Button>
          </span>
        </footer>
      </div>

      {openId !== null && Number.isFinite(openId) ? (
        <aside className="flex w-full min-w-0 flex-col border-line bg-surface lg:w-[28rem] lg:shrink-0 lg:border-l">
          <CallDetail key={openId} callId={openId} seed={seed} onClose={close} />
        </aside>
      ) : null}
    </div>
  );
}

/**
 * How fresh the history is, and why not, when it is not.
 *
 * The error text is an admin's: the server sends it only to them, because it
 * can name the credentials as the problem and an agent can do nothing about
 * that. Everybody is told the history has stopped, though -- a list that
 * silently stopped on Tuesday reads as a quiet week.
 */
function SyncLine({
  status,
  isAdmin,
}: {
  status: PhoneCallsStatus | undefined;
  isAdmin: boolean;
}) {
  const client = useQueryClient();
  const [queued, setQueued] = useState(false);

  const sync = useMutation({
    mutationFn: api.syncPhoneCalls,
    onSuccess: () => {
      setQueued(true);
      /* The pass runs in a worker, so there is nothing to await. Refetch once
         it has had time to finish rather than polling for it. */
      window.setTimeout(() => {
        setQueued(false);
        void client.invalidateQueries({ queryKey: queryKeys.phoneCallsRoot });
      }, SYNC_SETTLE_MS);
    },
  });

  if (!status?.enabled) return null;

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-[11px]">
      {status.failing ? (
        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          The phone system could not be read
          {status.lastErrorAt ? ` ${agoPhrase(status.lastErrorAt)}` : ""}
          {isAdmin && status.lastError ? `: ${status.lastError}` : ""}. Calls since
          then are missing until it recovers.
        </span>
      ) : (
        <span className="text-faint">
          {status.lastPassAt
            ? `Read from Yay ${agoPhrase(status.lastPassAt)}`
            : "Reading call history for the first time…"}
        </span>
      )}

      {isAdmin ? (
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          disabled={sync.isPending || queued}
          onClick={() => sync.mutate()}
        >
          {queued ? "Syncing…" : "Sync now"}
        </Button>
      ) : null}
    </div>
  );
}

function Filters({
  params,
  set,
  users,
}: {
  params: URLSearchParams;
  set: (patch: Record<string, string | null>) => void;
  users: User[] | undefined;
}) {
  /*
   * The search box is local until the typing stops. Writing every keystroke
   * into the URL would refetch on every one, and the search is across names
   * and number fragments where the half-typed version matches nonsense.
   */
  const [text, setText] = useState(params.get("q") ?? "");
  const settled = useDebounced(text, 300);
  const fromUrl = params.get("q") ?? "";

  useEffect(() => {
    if (settled.trim() !== fromUrl) set({ q: settled.trim() || null });
    // `set` and `fromUrl` change with every navigation; only the typing matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  /* A back-button or a pasted link changes the URL under the box. */
  useEffect(() => {
    if (fromUrl !== text.trim()) setText(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  /*
   * On a phone everything but the search box waits behind a button. Three
   * selects, a toggle and two date boxes wrapped into four rows of controls before the
   * first call -- most of a handset screen spent on filters that are mostly
   * left alone. The button counts what is set, so a narrowed list never
   * looks like a short one. A desktop sees the one row, as before.
   */
  const [showFilters, setShowFilters] = useState(false);
  const applied = ["direction", "outcome", "agentId", "recorded", "from", "to"].filter(
    (key) => params.get(key),
  ).length;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
      <input
        type="search"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Number, contact, company or agent"
        aria-label="Search calls"
        className="h-8 min-w-0 flex-1 md:h-7 md:w-56 md:flex-none rounded-md border border-line bg-surface px-2 text-xs text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
      />

      <button
        type="button"
        onClick={() => setShowFilters((open) => !open)}
        aria-expanded={showFilters}
        className={cn(
          "h-8 shrink-0 rounded-md border px-2.5 text-xs md:hidden",
          applied > 0
            ? "border-brand-500 bg-brand-50 text-content dark:bg-brand-950/40"
            : "border-line text-muted",
        )}
      >
        Filters{applied > 0 ? ` (${applied})` : ""}
      </button>

      <div className={cn(showFilters ? "flex w-full flex-wrap items-center gap-2" : "hidden", "md:contents")}>
      <Select
        value={params.get("direction") ?? ""}
        onChange={(value) => set({ direction: value })}
        label="Direction"
        options={[
          { value: "", label: "Any direction" },
          { value: "inbound", label: "Inbound" },
          { value: "outbound", label: "Outbound" },
          { value: "internal", label: "Internal" },
        ]}
      />

      <Select
        value={params.get("outcome") ?? ""}
        onChange={(value) => set({ outcome: value })}
        label="Outcome"
        options={[
          { value: "", label: "Any outcome" },
          { value: "answered", label: "Answered" },
          { value: "missed", label: "Missed (inbound)" },
        ]}
      />

      <Select
        value={params.get("agentId") ?? ""}
        onChange={(value) => set({ agentId: value })}
        label="Agent"
        options={[
          { value: "", label: "Any agent" },
          /* For finding the phone users that still need linking to somebody
             in Settings -- the calls an agent's figures are missing. */
          { value: "0", label: "Not linked to an agent" },
          ...(users ?? []).map((person) => ({
            value: String(person.id),
            label: person.name,
          })),
        ]}
      />

      <Toggle
        on={params.get("recorded") === "true"}
        onClick={() => set({ recorded: params.get("recorded") === "true" ? null : "true" })}
      >
        Recorded
      </Toggle>

      <span className="ml-auto flex items-center gap-1 text-[11px] text-faint">
        <input
          type="date"
          value={params.get("from") ?? ""}
          onChange={(event) => set({ from: event.target.value })}
          aria-label="From"
          className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
        />
        <span>–</span>
        <input
          type="date"
          value={params.get("to") ?? ""}
          onChange={(event) => set({ to: event.target.value })}
          aria-label="To"
          className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-content"
        />
      </span>
      </div>
    </div>
  );
}

function CallTable({
  rows,
  openId,
  onOpen,
}: {
  rows: PhoneCall[];
  openId: number | null;
  onOpen: (id: number) => void;
}) {
  return (
    <table className="w-full table-fixed text-xs">
      <thead className="sticky top-0 z-10 bg-app text-left text-[11px] uppercase tracking-wide text-faint">
        <tr className="border-b border-line">
          {/* Narrower on a phone, where "With" -- the one column that says
              who -- was left about seventy pixels. The direction there is
              its arrow alone; a missed call is still red. */}
          <th className="w-24 px-3 py-1.5 font-medium md:w-28 md:px-4">When</th>
          <th className="w-7 px-1 py-1.5 font-medium md:w-24 md:px-2">
            <span className="max-md:sr-only">Direction</span>
          </th>
          <th className="px-2 py-1.5 font-medium">With</th>
          <th className="hidden w-40 px-2 py-1.5 font-medium md:table-cell">Agent</th>
          <th className="hidden w-32 px-2 py-1.5 font-medium xl:table-cell">Line</th>
          <th className="w-16 px-2 py-1.5 text-right font-medium">Length</th>
          <th className="w-8 px-2 py-1.5" aria-label="Recording" />
        </tr>
      </thead>
      <tbody>
        {rows.map((call) => (
          <tr
            key={call.id}
            onClick={() => onOpen(call.id)}
            className={cn(
              "cursor-pointer odd:bg-surface even:bg-app hover:bg-surface-2",
              openId === call.id && "!bg-surface-2",
            )}
          >
            <td className="truncate px-3 py-1.5 tabular-nums text-muted md:px-4">
              {/* A real link for keyboard and middle-click; the row's click
                  is the convenience for everybody else. */}
              <a
                href={`/calls/${call.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onOpen(call.id);
                }}
                className="hover:underline"
              >
                {shortDateTime(call.startedAt)}
              </a>
            </td>
            <td className="px-1 py-1.5 md:px-2">
              <DirectionMark call={call} />
            </td>
            <td className="truncate px-2 py-1.5">
              <Party call={call} />
            </td>
            <td className="hidden truncate px-2 py-1.5 md:table-cell">
              <Agent call={call} />
            </td>
            <td className="hidden truncate px-2 py-1.5 text-muted xl:table-cell">
              {call.line ? formatPhone(call.line) : ""}
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-content">
              {callDuration(call)}
            </td>
            <td className="px-2 py-1.5 text-center text-faint">
              {call.hasRecording ? (
                <span
                  title={call.canHear ? "Recorded" : "Recorded — for admins and the agent who handled it"}
                  className={cn(call.canHear ? "text-brand-600 dark:text-brand-400" : "text-faint")}
                >
                  ●
                </span>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** An arrow and a word. A missed call is red, because it is the one to act on. */
function DirectionMark({ call }: { call: PhoneCall }) {
  const arrow: Record<PhoneCallDirection, string> = {
    inbound: "↙",
    outbound: "↗",
    internal: "↔",
    unknown: "·",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        call.missed
          ? "font-medium text-red-600 dark:text-red-400"
          : call.answered
            ? "text-content"
            : "text-faint",
      )}
      title={call.disposition ?? undefined}
    >
      <span aria-hidden>{arrow[call.direction]}</span>
      <span className="max-md:sr-only">
        {call.missed ? "Missed" : directionLabel(call).replace(", no answer", "")}
      </span>
    </span>
  );
}

/** Who the other end was: the CRM's name for them if it has one, the number if not. */
function Party({ call }: { call: PhoneCall }) {
  if (!call.remote) return <span className="text-faint">{partyName(call)}</span>;

  const known = call.remote.contact || call.remote.company;

  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className={cn("truncate", known ? "font-medium text-content" : "tabular-nums text-content")}>
        {partyName(call)}
      </span>
      <span className="truncate text-[11px] text-faint">
        {known
          ? [call.remote.contact && call.remote.company ? call.remote.company.name : null, formatPhone(call.remote.number)]
              .filter(Boolean)
              .join(" · ")
          : (call.remote.area ?? "")}
      </span>
    </span>
  );
}

function Agent({ call }: { call: PhoneCall }) {
  if (!call.agent) return <span className="text-faint">—</span>;

  return (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className="truncate text-content">{call.agent.name ?? `ext. ${call.agent.extension}`}</span>
      {!call.agent.linked ? (
        <span
          className="shrink-0 rounded bg-surface-2 px-1 text-[9px] uppercase text-faint"
          title="Link this phone user to somebody in Settings → Users"
        >
          Not linked
        </span>
      ) : null}
    </span>
  );
}

function Toggle({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        "h-7 rounded-md border px-2 text-xs transition-colors",
        on
          ? "border-brand-600 bg-brand-600 font-medium text-white"
          : "border-line bg-surface text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      {children}
    </button>
  );
}

/**
 * The URL's filters as the API's query.
 *
 * Dates are whole days in the browser's own zone -- the desk's, in practice --
 * and the end is exclusive: "to the 3rd" means through the end of the 3rd, so
 * it is sent as midnight on the 4th. Anything unreadable is dropped rather
 * than sent, for `readView`'s reason: a typo in a pasted link should give the
 * unfiltered list, not an error.
 */
function toQuery(params: URLSearchParams): PhoneCallListParams {
  const day = (value: string | null, offsetDays = 0): string | undefined => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
    const [year, month, date] = value.split("-").map(Number) as [number, number, number];
    const at = new Date(year, month - 1, date + offsetDays);
    return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
  };

  const direction = params.get("direction");
  const outcome = params.get("outcome");
  const agentId = params.get("agentId");
  const page = Number(params.get("page") ?? 1);

  return {
    q: params.get("q")?.trim() || undefined,
    from: day(params.get("from")),
    to: day(params.get("to"), 1),
    direction:
      direction === "inbound" || direction === "outbound" || direction === "internal"
        ? direction
        : undefined,
    outcome: outcome === "answered" || outcome === "missed" ? outcome : undefined,
    recorded: params.get("recorded") === "true" ? true : undefined,
    agentId: agentId !== null && /^\d+$/.test(agentId) ? Number(agentId) : undefined,
    page: Number.isInteger(page) && page > 1 ? page : 1,
    pageSize: PAGE_SIZE,
  };
}

function hasFilters(params: URLSearchParams): boolean {
  return ["q", "from", "to", "direction", "outcome", "recorded", "agentId"].some((key) =>
    params.has(key),
  );
}
